import React from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { defaultTooltipConfig, defaultGridConfig, defaultLegendConfig, defaultScaleTitleConfig } from '../../utils/chartConfig';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const COLOURS = {
  portfolio: '#305496',
  pension: '#FFC000',
  employment: '#808080',
  other: '#011E41',
  rental: '#AA8090',
  annuity: '#91CF9B',
  lumpSum: '#599BE3',
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

const IncomeBreakdownChart = React.forwardRef(({ data, years, age1 }, ref) => {
  const labels = years.map((y) => `${y}`);

  const makeSeries = (label, arr, colour) => {
    if (!arr || arr.every((v) => v === 0 || v == null)) return null;
    return {
      label,
      data: arr,
      backgroundColor: colour,
      borderWidth: 0,
    };
  };

  const datasets = [
    makeSeries('Portfolio Income', data.portfolio_income, COLOURS.portfolio),
    makeSeries('Pension', data.pension_income, COLOURS.pension),
    makeSeries('Employment', data.employment_income, COLOURS.employment),
    makeSeries('Other', data.other_income, COLOURS.other),
    makeSeries('Rental', data.rental_income, COLOURS.rental),
    makeSeries('Annuity', data.income_stream, COLOURS.annuity),
    makeSeries('Lump Sum', data.lump_sum_income, COLOURS.lumpSum),
  ].filter(Boolean);

  const chartData = { labels, datasets };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index',
      intersect: false,
    },
    plugins: {
      title: {
        display: true,
        text: 'Annual Income Breakdown',
        font: { size: 14 },
      },
      legend: defaultLegendConfig,
      tooltip: {
        ...defaultTooltipConfig,
        callbacks: {
          title: (items) => {
            const idx = items[0]?.dataIndex;
            const year = years[idx];
            const age = age1 != null ? age1 + year : null;
            return age != null ? `Year ${year} (Age ${age})` : `Year ${year}`;
          },
          label: (item) => {
            return `${item.dataset.label}: ${formatCurrency(item.raw)}`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Age',
        },
        ticks: {
          callback: function (value, index) {
            const year = years[index];
            if (age1 != null) {
              return `${age1 + year}`;
            }
            return `${year}`;
          },
          maxRotation: 0,
          autoSkip: true,
          maxTicksLimit: 15,
        },
      },
      y: {
        stacked: true,
        grid: defaultGridConfig,
        title: {
          ...defaultScaleTitleConfig,
          text: 'Income ($)',
        },
        ticks: {
          callback: (value) => formatCurrency(value),
        },
      },
    },
  };

  return (
    <div style={{ height: '350px', position: 'relative' }}>
      <Bar ref={ref} data={chartData} options={options} />
    </div>
  );
});

IncomeBreakdownChart.displayName = 'IncomeBreakdownChart';

export default IncomeBreakdownChart;
